import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getUserName, clearLegacyTokens } from './services/auth';
import { NamePrompt } from './components/NamePrompt';

import { api } from './services/api';
import { useProperties } from './hooks/useProperties';
import { TierProvider, useTier } from './hooks/useTier';
import { PropertyList } from './components/PropertyList';
import { PropertyDetail } from './components/PropertyDetail';
import { CompareView } from './components/CompareView';
import { ReadOnlyBanner } from './components/ReadOnlyBanner';
import { TrialExpiryBanner } from './components/TrialExpiryBanner';
import { SearchSwitcher } from './components/SearchSwitcher';
import { InviteSection } from './components/InviteSection';
import { EmptyState } from './components/EmptyState';
import { CreateSearchWizard } from './components/CreateSearchWizard';
import { RequestSiteModal } from './components/RequestSiteModal';
import { ProfileSettings } from './components/ProfileSettings';
import { SearchSettings } from './components/SearchSettings';
import { AddPropertyForm } from './components/AddPropertyForm';
import { Spinner } from './components/index';
import { Settings, MessageCircle, UserCircle } from 'lucide-react';
import type { FlatSearch, Location } from './types/api';
import { ChatPanel } from './components/ChatPanel';
import { HelpMenu } from './components/HelpMenu';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import './App.css';
import { ENABLE_AI_CHAT } from './config';
import { trackEvent } from './services/analytics';

const PENDING_INVITE_KEY = 'movetwo_pending_invite';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

function Dashboard() {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const { refresh: refreshTier } = useTier();
  const [searches, setSearches] = useState<FlatSearch[]>([]);
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null);
  const [searchesLoading, setSearchesLoading] = useState(true);
  const [joinMessage, setJoinMessage] = useState<{ text: string; isError: boolean } | null>(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showRequestSite, setShowRequestSite] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSearchSettings, setShowSearchSettings] = useState(false);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [pendingDeepLink, setPendingDeepLink] = useState<{ searchId: string; propertyId: string } | null>(null);
  const deepLinkAppliedRef = useRef(false);
  const [primaryLocationName, setPrimaryLocationName] = useState<string | undefined>(undefined);

  const loadSearches = useCallback(async () => {
    setSearchesLoading(true);
    try {
      const data = await api.listSearches();
      setSearches(data);
      if (data.length > 0 && !selectedSearchId) {
        const active = data.find((s) => s.status === 'active');
        setSelectedSearchId(active ? active.id : data[0].id);
      }
    } catch {
      // Searches failed to load
    } finally {
      setSearchesLoading(false);
    }
  }, [selectedSearchId]);

  // Load primary location name
  useEffect(() => {
    if (!selectedSearchId) { setPrimaryLocationName(undefined); return; }
    let cancelled = false;
    api.getLocations(selectedSearchId).then((locs: Location[]) => {
      if (cancelled) return;
      const primary = locs.find(l => l.isPrimary);
      setPrimaryLocationName(primary?.name ?? locs[0]?.name);
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [selectedSearchId]);

  const handlePendingInvite = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    let inviteCode = params.get('invite');
    if (!inviteCode) inviteCode = localStorage.getItem(PENDING_INVITE_KEY);
    if (!inviteCode) return;
    if (params.has('invite')) {
      params.delete('invite');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    try {
      const joinedSearch = await api.joinSearch(inviteCode);
      localStorage.removeItem(PENDING_INVITE_KEY);
      setSearches((prev) => {
        const exists = prev.some((s) => s.id === joinedSearch.id);
        return exists ? prev.map((s) => (s.id === joinedSearch.id ? joinedSearch : s)) : [...prev, joinedSearch];
      });
      setSelectedSearchId(joinedSearch.id);
      setJoinMessage({ text: t('app.joinMessage.joined', { name: joinedSearch.name }), isError: false });
      setTimeout(() => setJoinMessage(null), 5000);
    } catch {
      setJoinMessage({ text: t('app.joinMessage.failedToJoin'), isError: true });
    }
  }, [t]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let searchParam = params.get('search');
    let propertyParam = params.get('property');
    // Fallback to localStorage (persisted before auth redirect)
    if (!searchParam || !propertyParam) {
      try {
        const stored = localStorage.getItem('movetwo_pending_deeplink');
        if (stored) {
          const parsed = JSON.parse(stored);
          searchParam = parsed.searchId;
          propertyParam = parsed.propertyId;
        }
      } catch { /* ignore */ }
    }
    if (searchParam && propertyParam) {
      setPendingDeepLink({ searchId: searchParam, propertyId: propertyParam });
    }
    localStorage.removeItem('movetwo_pending_deeplink');
  }, []);

  // Handle ?view=settings / ?view=search-settings deep-link (from extension)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view === 'settings') {
      setShowSettings(true);
      params.delete('view');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } else if (view === 'search-settings') {
      setShowSearchSettings(true);
      params.delete('view');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Handle Stripe checkout success redirect
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      setCheckoutSuccess(true);
      trackEvent('subscription_started');
      // Webhook may not have fired yet — retry with delays
      refreshTier();
      setTimeout(() => refreshTier(), 3000);
      setTimeout(() => refreshTier(), 8000);
      params.delete('checkout');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      setTimeout(() => setCheckoutSuccess(false), 10000);
    }
  }, [refreshTier]);

  useEffect(() => {
    loadSearches().then(() => handlePendingInvite());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    properties, loading, error, selectedProperty, selectedPropertyLoading, statusFilter,
    setStatusFilter, selectProperty, createProperty, updateProperty, deleteProperty, refresh, tierInfo,
    compareIds, toggleCompare, clearCompare, loadedForSearchId,
  } = useProperties(selectedSearchId);

  useEffect(() => {
    if (!pendingDeepLink || deepLinkAppliedRef.current || searchesLoading || loading) return;
    const matchingSearch = searches.find((s) => s.id === pendingDeepLink.searchId);
    if (!matchingSearch) { setPendingDeepLink(null); window.history.replaceState({}, '', '/'); return; }
    if (selectedSearchId !== pendingDeepLink.searchId) { setSelectedSearchId(pendingDeepLink.searchId); return; }
    // Wait until properties have actually been loaded for the deep link's search
    if (loadedForSearchId !== pendingDeepLink.searchId) return;
    const matchingProperty = properties.find((p) => p.id === pendingDeepLink.propertyId);
    if (matchingProperty) {
      deepLinkAppliedRef.current = true;
      selectProperty(matchingProperty.id);
      setPendingDeepLink(null);
      window.history.replaceState({}, '', `?search=${pendingDeepLink.searchId}&property=${pendingDeepLink.propertyId}`);
    } else {
      setPendingDeepLink(null);
      window.history.replaceState({}, '', '/');
    }
  }, [pendingDeepLink, searches, searchesLoading, properties, loading, selectedSearchId, selectProperty, setSelectedSearchId, loadedForSearchId]);

  const selectedSearch = searches.find((s) => s.id === selectedSearchId) ?? null;
  const hasActiveSearch = searches.some((s) => s.status === 'active');

  const handleSearchUpdate = useCallback((updated: FlatSearch) => {
    setSearches((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  }, []);

  const openPropertyModal = useCallback((propertyId: string) => {
    selectProperty(propertyId);
    if (selectedSearchId) window.history.pushState({}, '', `?search=${selectedSearchId}&property=${propertyId}`);
  }, [selectProperty, selectedSearchId]);

  const closePropertyModal = useCallback(() => {
    selectProperty(null);
    window.history.pushState({}, '', '/');
  }, [selectProperty]);

  const modalOpen = !!selectedProperty || selectedPropertyLoading;

  // Scroll lock when modal is open (mobile only)
  useEffect(() => {
    if (modalOpen && !isDesktop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [modalOpen, isDesktop]);

  // Escape key and browser back button to close modal/panel
  useEffect(() => {
    if (!modalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') closePropertyModal(); };
    const handlePopState = () => { selectProperty(null); };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [modalOpen, closePropertyModal, selectProperty]);

  // Listen for open-settings events from child components
  useEffect(() => {
    const handler = () => setShowSettings(true);
    window.addEventListener('open-settings', handler);
    return () => window.removeEventListener('open-settings', handler);
  }, []);

  if (showSettings) {
    return (
      <div className="dashboard">
        <header className="dashboard-header">
          <h1>{t('app.header.title')}</h1>
          <button className="settings-btn" onClick={() => setShowSettings(false)} aria-label={t('common.close')}>
            <Settings size={20} />
          </button>
        </header>
        <ProfileSettings searchId={selectedSearchId} onBack={() => setShowSettings(false)} />
      </div>
    );
  }

  if (showSearchSettings && selectedSearchId && selectedSearch) {
    return (
      <div className="dashboard">
        <header className="dashboard-header">
          <h1>{t('app.header.title')}</h1>
          <button className="settings-btn" onClick={() => setShowSearchSettings(false)} aria-label={t('common.close')}>
            <Settings size={20} />
          </button>
        </header>
        <SearchSettings
          search={selectedSearch}
          onBack={() => setShowSearchSettings(false)}
          onSearchUpdate={handleSearchUpdate}
        />
      </div>
    );
  }

  if (showCompare && selectedSearchId) {
    return (
      <div className="dashboard">
        <CompareView
          propertyIds={Array.from(compareIds)}
          searchId={selectedSearchId}
          onClose={() => setShowCompare(false)}
          onRemove={(id) => { toggleCompare(id); if (compareIds.size <= 1) setShowCompare(false); }}
          onUpdate={updateProperty}
        />
      </div>
    );
  }

  const panelOpen = isDesktop && modalOpen;

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>{t('app.header.title')}</h1>
        <div className="header-actions">
          <LanguageSwitcher />
          <button className="settings-btn" onClick={() => setShowSettings(true)} aria-label={t('common.settings')}>
            <UserCircle size={20} />
          </button>
        </div>
      </header>
      {joinMessage && (
        <div className={`join-message ${joinMessage.isError ? 'join-message-error' : ''}`}>
          <span>{joinMessage.text}</span>
          {joinMessage.isError && (
            <button className="join-message-dismiss" onClick={() => setJoinMessage(null)} aria-label={t('common.dismiss')}>&times;</button>
          )}
        </div>
      )}
      {checkoutSuccess && (
        <div className="join-message">
          <span>{t('app.checkoutSuccess')}</span>
          <button className="join-message-dismiss" onClick={() => setCheckoutSuccess(false)} aria-label={t('common.dismiss')}>&times;</button>
        </div>
      )}
      <ReadOnlyBanner />
      <TrialExpiryBanner />
      <div className="search-header-row">
        <SearchSwitcher
          searches={searches}
          selectedSearch={selectedSearch}
          loading={searchesLoading}
          onSelect={setSelectedSearchId}
          onCreateSearch={hasActiveSearch ? undefined : () => setShowCreateWizard(true)}
          onSearchJoined={(search) => {
            setSearches((prev) => { const exists = prev.some((s) => s.id === search.id); return exists ? prev : [...prev, search]; });
            setSelectedSearchId(search.id);
            setJoinMessage({ text: t('app.joinMessage.joined', { name: search.name }), isError: false });
            setTimeout(() => setJoinMessage(null), 5000);
          }}
        />
        {selectedSearchId && (
          <button className="search-settings-btn" onClick={() => setShowSearchSettings(true)} aria-label={t('app.searchSettings.heading')}>
            <Settings size={18} />
          </button>
        )}
      </div>
      {selectedSearch && !showCreateWizard && (
        <InviteSection key={selectedSearch.id} search={selectedSearch} properties={properties} onSearchUpdate={handleSearchUpdate} />
      )}
      {showCreateWizard && (
        <CreateSearchWizard
          onComplete={(search) => { setSearches((prev) => [...prev, search]); setSelectedSearchId(search.id); setShowCreateWizard(false); trackEvent('search_created', { search_id: search.id }); }}
          onCancel={() => setShowCreateWizard(false)}
        />
      )}
      {!showCreateWizard && !selectedSearchId && !searchesLoading && searches.length === 0 && (
        <EmptyState
          onSearchCreated={(search) => { setSearches((prev) => [...prev, search]); setSelectedSearchId(search.id); trackEvent('search_created', { search_id: search.id }); }}
          onSearchJoined={(search) => {
            setSearches((prev) => { const exists = prev.some((s) => s.id === search.id); return exists ? prev : [...prev, search]; });
            setSelectedSearchId(search.id);
            setJoinMessage({ text: t('app.joinMessage.joined', { name: search.name }), isError: false });
            setTimeout(() => setJoinMessage(null), 5000);
          }}
        />
      )}
      {!showCreateWizard && !selectedSearchId && !searchesLoading && searches.length > 0 && (
        <div className="empty-state"><p>{t('app.selectSearch')}</p></div>
      )}

      {/* Main content area: list + side panel */}
      {!showCreateWizard && selectedSearchId && (
        <div className={`app-layout${panelOpen ? ' app-layout-panel-open' : ''}`}>
          <div className="app-layout-list">
            <PropertyList
              properties={properties}
              loading={loading}
              error={error}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onPropertyClick={openPropertyModal}
              onRetry={refresh}
              tierInfo={tierInfo}
              compareIds={compareIds}
              onToggleCompare={toggleCompare}
              primaryLocationName={primaryLocationName}
              activePropertyId={selectedProperty?.id}
              onAddProperty={() => setShowAddProperty(true)}
            />
            <div style={{ textAlign: 'center', padding: '8px 16px 16px' }}>
              <button className="request-site-link" onClick={() => setShowRequestSite(true)}>
                {t('app.requestSiteLink')}
              </button>
            </div>
          </div>

          {/* Desktop side panel */}
          {panelOpen && (
            <div className="side-panel">
              {selectedPropertyLoading ? (
                <div className="loading-state" style={{ padding: '48px 0' }}>
                  <Spinner />
                  <p>{t('app.loadingProperty')}</p>
                </div>
              ) : selectedProperty ? (
                <PropertyDetail
                  property={selectedProperty}
                  searchId={selectedSearchId}
                  members={selectedSearch?.members ?? []}
                  onBack={closePropertyModal}
                  onUpdate={updateProperty}
                  onDelete={deleteProperty}
                  readOnly={selectedSearch?.status !== 'active' || selectedProperty.locked}
                />
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Compare bottom bar */}
      {compareIds.size > 0 && (
        <div className="compare-bottom-bar">
          <span>{t('app.selected', { count: compareIds.size })}</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowCompare(true)}>{t('app.compare')}</button>
          <button className="btn btn-ghost btn-sm" onClick={clearCompare}>{t('app.clear')}</button>
        </div>
      )}

      <RequestSiteModal isOpen={showRequestSite} onClose={() => setShowRequestSite(false)} />
      <AddPropertyForm
        isOpen={showAddProperty}
        onClose={() => setShowAddProperty(false)}
        onCreate={createProperty}
        onCreated={(id) => selectProperty(id)}
      />

      {/* Mobile modal overlay */}
      {!isDesktop && (selectedProperty || selectedPropertyLoading) && (
        <div className="property-detail-overlay" onClick={closePropertyModal}>
          <div className="property-detail-modal" role="dialog" aria-modal="true" aria-label={t('app.loadingProperty')} onClick={(e) => e.stopPropagation()}>
            {selectedPropertyLoading ? (
              <div className="loading-state" style={{ padding: '48px 0' }}>
                <Spinner />
                <p>{t('app.loadingProperty')}</p>
              </div>
            ) : selectedProperty ? (
              <PropertyDetail
                property={selectedProperty}
                searchId={selectedSearchId}
                members={selectedSearch?.members ?? []}
                onBack={closePropertyModal}
                onUpdate={updateProperty}
                onDelete={deleteProperty}
                readOnly={selectedSearch?.status !== 'active' || selectedProperty.locked}
              />
            ) : null}
          </div>
        </div>
      )}

      {/* Chat panel + toggle (hidden when AI features are disabled) */}
      {ENABLE_AI_CHAT && selectedSearchId && showChat && (
        <ChatPanel
          searchId={selectedSearchId}
          onClose={() => setShowChat(false)}
          onAction={() => { refresh(); void loadSearches(); }}
        />
      )}
      {ENABLE_AI_CHAT && selectedSearchId && !showChat && (
        <button className="chat-fab" onClick={() => setShowChat(true)} aria-label={t('app.chatPanel.title')}>
          <MessageCircle size={24} />
        </button>
      )}

      <HelpMenu />
    </div>
  );
}

function AuthenticatedApp() {
  const [needsName, setNeedsName] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    clearLegacyTokens();
    const params = new URLSearchParams(window.location.search);
    const inviteCode = params.get('invite');
    if (inviteCode) localStorage.setItem(PENDING_INVITE_KEY, inviteCode);
    // Persist deep link params so they survive auth redirects
    const searchParam = params.get('search');
    const propertyParam = params.get('property');
    if (searchParam && propertyParam) {
      localStorage.setItem('movetwo_pending_deeplink', JSON.stringify({ searchId: searchParam, propertyId: propertyParam }));
    }
    getUserName().then((name) => {
      setNeedsName(!name);
      setChecking(false);
      // Only fire signup_complete on the very first session (no searches yet)
      if (!sessionStorage.getItem('movetwo_signup_tracked')) {
        sessionStorage.setItem('movetwo_signup_tracked', '1');
        trackEvent('signup_complete');
      }
    });
  }, []);

  if (checking) {
    return <div className="app loading-app"><Spinner /></div>;
  }

  return (
    <TierProvider>
      <div className="app">
        {needsName && <NamePrompt onComplete={() => setNeedsName(false)} />}
        <Dashboard />
      </div>
    </TierProvider>
  );
}

export default function App() {
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated' | 'error'>('loading');
  const redirectAttempted = useRef(false);

  useEffect(() => {
    // Persist deep link params BEFORE any redirect
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get('search');
    const propertyParam = params.get('property');
    if (searchParam && propertyParam) {
      localStorage.setItem('movetwo_pending_deeplink', JSON.stringify({ searchId: searchParam, propertyId: propertyParam }));
    }
    const inviteCode = params.get('invite');
    if (inviteCode) localStorage.setItem(PENDING_INVITE_KEY, inviteCode);

    // Listen for redirect callback completion
    const hubListener = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        setAuthState('authenticated');
      }
      if (payload.event === 'signInWithRedirect_failure') {
        setAuthState('error');
      }
    });

    // Check current session
    fetchAuthSession()
      .then((session) => {
        if (session.tokens?.idToken) {
          setAuthState('authenticated');
        } else {
          setAuthState('unauthenticated');
        }
      })
      .catch(() => {
        setAuthState('unauthenticated');
      });

    return hubListener;
  }, []);

  useEffect(() => {
    if (authState === 'unauthenticated' && !redirectAttempted.current) {
      redirectAttempted.current = true;
      signInWithRedirect();
    }
  }, [authState]);

  if (authState === 'error') {
    return (
      <div className="app loading-app">
        <p style={{ textAlign: 'center', color: 'var(--gray-600)' }}>
          Sign-in failed. Please try again.
        </p>
        <button className="btn btn-primary btn-sm" onClick={() => { redirectAttempted.current = false; setAuthState('unauthenticated'); }}>
          Sign In
        </button>
      </div>
    );
  }

  if (authState !== 'authenticated') {
    return <div className="app loading-app"><Spinner /></div>;
  }

  return <AuthenticatedApp />;
}
